import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtInt } from "@/api";
import { useI18n } from "@/i18n";
import { useCurrency } from "@/currency";
import { useShowPrices } from "@/showPrices";
import { useAuth } from "@/auth";
import { PageHeader, Tile, Loading } from "@/components/Bits";
import { Target, Trophy, CalendarCheck, Percent, FloppyDisk, Medal, ChartLineUp } from "@phosphor-icons/react";

function ProgressBar({ value, target, color = "var(--accent)", testId }) {
  const pct = target > 0 ? Math.min(120, (value / target) * 100) : 0;
  const overshoot = pct > 100;
  const w = Math.min(100, pct);
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--bg-subtle)" }}>
        <div
          className="h-full transition-all duration-700 ease-out"
          style={{ width: `${w}%`, background: overshoot ? "#34A853" : color }}
        />
      </div>
      <div className="flex items-center justify-between text-[11px] font-mono" style={{ color: "var(--text-tertiary)" }}>
        <span>{pct.toFixed(0)}%</span>
        <span>{overshoot ? "🏆" : ""}</span>
      </div>
    </div>
  );
}

function MetricCard({ Icon, label, value, sub, color = "var(--text-primary)", testId }) {
  return (
    <div className="tile p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} weight="fill" style={{ color }} />
        <span className="label-tech">{label}</span>
      </div>
      <div className="num text-[28px] font-semibold leading-none" style={{ color, letterSpacing: "-0.025em" }}>
        {value}
      </div>
      {sub && <div className="mt-2 text-[11.5px]" style={{ color: "var(--text-tertiary)" }}>{sub}</div>}
    </div>
  );
}

export default function Objectives() {
  const { lang } = useI18n();
  const { fmt: fmtMoney } = useCurrency();
  const { showPrices } = useShowPrices();
  const { user } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [obj, setObj] = useState(null);
  const [team, setTeam] = useState(null);
  const [form, setForm] = useState({ quota_eur: 0, target_visits: 0, target_wins: 0 });
  const [saving, setSaving] = useState(false);

  // When the "Hide prices" mode is ON we never expose monetary amounts.
  // We use a discreet redaction marker so the layout stays identical.
  const money = (v) => (showPrices ? fmtMoney(v || 0) : "•••");

  const reload = (y = year) => {
    api.get("/objectives/me", { params: { year: y } }).then((r) => {
      setObj(r.data);
      setForm({
        quota_eur: r.data.quota_eur || 0,
        target_visits: r.data.target_visits || 0,
        target_wins: r.data.target_wins || 0,
      });
    });
    api.get("/objectives/team", { params: { year: y } }).then((r) => setTeam(r.data));
  };
  useEffect(() => reload(year), [year]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        year,
        quota_eur: Number(form.quota_eur) || 0,
        target_visits: Number(form.target_visits) || 0,
        target_wins: Number(form.target_wins) || 0,
      };
      await api.put("/objectives/me", body);
      toast.success(lang === "fr" ? "Objectifs enregistrés" : "Objectives saved");
      reload(year);
    } catch {
      toast.error(lang === "fr" ? "Échec de l'enregistrement" : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!obj || !team) return <Loading />;
  const p = obj.progress || {};
  const years = [year - 1, year, year + 1];

  return (
    <div data-testid="objectives-page">
      <PageHeader
        title={lang === "fr" ? "Mes objectifs" : "My objectives"}
        subtitle={lang === "fr"
          ? `${user?.name || user?.email} · ${obj.country} · Suivi annuel des quotas et performances`
          : `${user?.name || user?.email} · ${obj.country} · Annual quota & performance tracking`}
        right={
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            data-testid="objectives-year-select"
            className="px-3 py-1.5 text-[13px] rounded-[10px] font-mono"
            style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", color: "var(--text-primary)" }}
          >
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        }
      />
      <div className="p-4 md:p-8 space-y-6 md:space-y-8">

        {/* === Performance KPIs === */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
          <MetricCard
            Icon={Trophy} color="#34A853"
            label={lang === "fr" ? "Gagné (réel)" : "Won (actual)"}
            value={money(p.won_value_eur)}
            sub={`${p.won_count || 0} ${lang === "fr" ? "deal(s)" : "deal(s)"}`}
            testId="kpi-won"
          />
          <MetricCard
            Icon={Target} color="var(--accent)"
            label={lang === "fr" ? "Quota annuel" : "Annual quota"}
            value={money(obj.quota_eur)}
            sub={lang === "fr" ? `${(((p.won_value_eur || 0) / Math.max(1, obj.quota_eur)) * 100).toFixed(0)}% atteint` : `${(((p.won_value_eur || 0) / Math.max(1, obj.quota_eur)) * 100).toFixed(0)}% achieved`}
            testId="kpi-quota"
          />
          <MetricCard
            Icon={CalendarCheck} color="#0D71DD"
            label={lang === "fr" ? "Visites cette année" : "Visits this year"}
            value={fmtInt(p.visit_count || 0)}
            sub={obj.target_visits ? `${lang === "fr" ? "cible" : "target"}: ${obj.target_visits}` : (lang === "fr" ? "Aucune cible" : "No target")}
            testId="kpi-visits"
          />
          <MetricCard
            Icon={Percent} color="#F59E0B"
            label={lang === "fr" ? "Taux de conversion" : "Conversion rate"}
            value={p.conversion_pct != null ? `${p.conversion_pct}%` : "—"}
            sub={`${p.won_count || 0} ${lang === "fr" ? "gagnées / " : "won / "}${p.lost_count || 0} ${lang === "fr" ? "perdues" : "lost"}`}
            testId="kpi-conversion"
          />
        </div>

        {/* === Progress bars === */}
        <Tile testId="objectives-progress">
          <div className="flex items-center gap-2 mb-5">
            <ChartLineUp size={16} weight="fill" style={{ color: "var(--accent)" }} />
            <h2 className="text-[15px] font-semibold tracking-tight" style={{ letterSpacing: "-0.02em" }}>
              {lang === "fr" ? "Progression" : "Progress"}
            </h2>
          </div>
          <div className="space-y-5">
            {showPrices && (
            <div>
              <div className="flex justify-between text-[12.5px] mb-1.5">
                <span style={{ color: "var(--text-secondary)" }}>
                  {lang === "fr" ? "Quota €" : "€ Quota"}
                </span>
                <span className="font-mono" style={{ color: "var(--text-primary)" }}>
                  {money(p.won_value_eur)} <span style={{ color: "var(--text-tertiary)" }}>/ {money(obj.quota_eur)}</span>
                </span>
              </div>
              <ProgressBar value={p.won_value_eur || 0} target={obj.quota_eur || 0} testId="progress-quota" />
            </div>
            )}
            <div>
              <div className="flex justify-between text-[12.5px] mb-1.5">
                <span style={{ color: "var(--text-secondary)" }}>
                  {lang === "fr" ? "Deals gagnés (#)" : "Won deals (#)"}
                </span>
                <span className="font-mono" style={{ color: "var(--text-primary)" }}>
                  {p.won_count || 0} <span style={{ color: "var(--text-tertiary)" }}>/ {obj.target_wins || 0}</span>
                </span>
              </div>
              <ProgressBar value={p.won_count || 0} target={obj.target_wins || 0} color="#34A853" testId="progress-wins" />
            </div>
            <div>
              <div className="flex justify-between text-[12.5px] mb-1.5">
                <span style={{ color: "var(--text-secondary)" }}>
                  {lang === "fr" ? "Visites (#)" : "Visits (#)"}
                </span>
                <span className="font-mono" style={{ color: "var(--text-primary)" }}>
                  {p.visit_count || 0} <span style={{ color: "var(--text-tertiary)" }}>/ {obj.target_visits || 0}</span>
                </span>
              </div>
              <ProgressBar value={p.visit_count || 0} target={obj.target_visits || 0} color="#0D71DD" testId="progress-visits" />
            </div>
          </div>
        </Tile>

        {/* === Edit objectives === */}
        <Tile testId="objectives-edit">
          <div className="flex items-center gap-2 mb-4">
            <Target size={16} weight="fill" style={{ color: "var(--accent)" }} />
            <h2 className="text-[15px] font-semibold tracking-tight" style={{ letterSpacing: "-0.02em" }}>
              {lang === "fr" ? `Définir mes objectifs ${year}` : `Set my ${year} objectives`}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { key: "quota_eur",      label: lang === "fr" ? "Quota annuel (€)" : "Annual quota (€)",    placeholder: "5000000" },
              { key: "target_wins",    label: lang === "fr" ? "Deals à gagner"   : "Wins target",          placeholder: "8" },
              { key: "target_visits",  label: lang === "fr" ? "Visites cibles"   : "Visits target",        placeholder: "40" },
            ].filter(({ key }) => showPrices || key !== "quota_eur").map(({ key, label, placeholder }) => (
              <label key={key} className="block">
                <div className="text-[11px] mb-1.5 font-mono uppercase tracking-wide" style={{ color: "var(--text-tertiary)" }}>{label}</div>
                <input
                  type="number"
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  data-testid={`objective-input-${key}`}
                  placeholder={placeholder}
                  className="w-full px-3 py-2 text-[14px] font-mono rounded-[10px]"
                  style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", color: "var(--text-primary)" }}
                />
              </label>
            ))}
          </div>
          <div className="mt-5 flex justify-end">
            <button
              onClick={save}
              disabled={saving}
              data-testid="objectives-save-btn"
              className="btn-accent flex items-center gap-2"
            >
              <FloppyDisk size={14} weight="bold" />
              {saving
                ? (lang === "fr" ? "Enregistrement…" : "Saving…")
                : (lang === "fr" ? "Enregistrer" : "Save")}
            </button>
          </div>
        </Tile>

        {/* === Team leaderboard === */}
        <Tile testId="objectives-leaderboard">
          <div className="flex items-center gap-2 mb-4">
            <Medal size={16} weight="fill" style={{ color: "#F59E0B" }} />
            <h2 className="text-[15px] font-semibold tracking-tight" style={{ letterSpacing: "-0.02em" }}>
              {lang === "fr" ? `Classement équipe ${year}` : `Team leaderboard ${year}`}
            </h2>
          </div>
          {team.rows.length === 0 ? (
            <div className="text-center py-10 text-[13px]" style={{ color: "var(--text-tertiary)" }}>
              {lang === "fr" ? "Pas encore d'activité cette année." : "No activity yet this year."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="tech w-full" data-testid="leaderboard-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{lang === "fr" ? "Utilisateur" : "User"}</th>
                    <th className="text-right">{showPrices ? (lang === "fr" ? "Gagné €" : "Won €") : (lang === "fr" ? "Gagné" : "Won")}</th>
                    <th className="text-right">{lang === "fr" ? "# Gagnées" : "# Won"}</th>
                    <th className="text-right">{lang === "fr" ? "# Perdues" : "# Lost"}</th>
                    <th className="text-right">{lang === "fr" ? "Visites" : "Visits"}</th>
                    <th className="text-right">{lang === "fr" ? "Conversion" : "Conv."}</th>
                  </tr>
                </thead>
                <tbody>
                  {team.rows.map((r, i) => (
                    <tr key={r.user_email}>
                      <td className="num">
                        {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                      </td>
                      <td>
                        <div className="font-medium">{r.name}</div>
                        <div className="text-[11px] font-mono" style={{ color: "var(--text-tertiary)" }}>{r.user_email}</div>
                      </td>
                      <td className="num font-medium text-wabtec-red">{money(r.won_value_eur)}</td>
                      <td className="num">{r.won_count}</td>
                      <td className="num">{r.lost_count}</td>
                      <td className="num">{r.visit_count}</td>
                      <td className="num">{r.conversion_pct != null ? `${r.conversion_pct}%` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Tile>

      </div>
    </div>
  );
}
